import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Handler from "../Helpers/Handler";
import { useAuth } from "./AuthenticateProvider";
import SessionWarning from "../Components/SessionWarning";
import {
    createActivityTracker,
    getSession,
    leaveToSignIn,
    onOtherTabSignedOut,
    phaseOf,
    saveSession,
    serverNow,
    subscribe,
} from "../Helpers/webSession";

// Staff web sign-in session (Staff Login Sessions plan, phase 2). Mounted inside AuthenticateProvider.
//  - tells the server when the person is really using the page (POST /staff/session/activity, at most once a minute,
//    from all tabs together); only that moves the 30-minute idle time - polling and the socket never do;
//  - keeps "when does this sign-in end" for the header and for the pop-ups (2 minutes before the idle sign-out,
//    10 minutes before the 12-hour limit); every tab of the browser shows the same;
//  - when the time is up it asks the server, which answers 401 with the reason (see Helpers/ApiCall).

const SessionContext = createContext(null);

// Not "scroll": a page can scroll by itself (a list refreshed by polling), and that must never count as activity.
// Wheel, touch, keys and pointer presses (scrollbar drags included) cover real scrolling.
const INPUT_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];
const CHECK_EVERY_MS = 10 * 1000; // asking the server again once the time is up

function SessionProvider({ children }) {
    const { isAuthenticated, logOut } = useAuth();
    const [session, setSession] = useState(getSession);
    const [now, setNow] = useState(() => Date.now());
    const [capDismissedFor, setCapDismissedFor] = useState(null);
    const trackerRef = useRef(null);
    const checkedAtRef = useRef(0);

    const readView = (response) => (response?.success ? response?.data?.data : null);

    // Reading the session is not activity, so this never keeps a session alive.
    const refresh = useCallback(async () => {
        const view = readView(await Handler({ method: "GET", url: "/staff/session" }));
        if (view) saveSession(view);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        setSession(getSession());
        const unsubscribe = subscribe(setSession);
        refresh(); // also covers a sign-in made before this page version (nothing stored yet)
        return unsubscribe;
    }, [isAuthenticated, refresh]);

    // Another tab of this browser signed out or lost its sign-in: nothing to do here but leave.
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        return onOtherTabSignedOut(leaveToSignIn);
    }, [isAuthenticated]);

    // Real input -> activity call.
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const tracker = createActivityTracker({
            send: async () => {
                const view = readView(await Handler({ method: "post", url: "/staff/session/activity", data: {} }));
                if (view) saveSession(view);
                return Boolean(view);
            },
        });
        trackerRef.current = tracker;
        const onInput = () => tracker.onInput();
        INPUT_EVENTS.forEach((name) => window.addEventListener(name, onInput, { capture: true, passive: true }));
        return () => {
            INPUT_EVENTS.forEach((name) => window.removeEventListener(name, onInput, { capture: true }));
            tracker.dispose();
            trackerRef.current = null;
        };
    }, [isAuthenticated]);

    const phase = useMemo(() => phaseOf(session, serverNow(session, now)), [session, now]);
    const showWarning = isAuthenticated && phase.warn;

    // A clock for the countdown: every second while a pop-up is open, otherwise only now and then.
    useEffect(() => {
        if (!isAuthenticated || !session) return undefined;
        const id = setInterval(() => setNow(Date.now()), showWarning ? 1000 : 15000);
        const onVisible = () => document.visibilityState === "visible" && setNow(Date.now());
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [isAuthenticated, session, showWarning]);

    // The time is up on this screen: the server has the final say (another tab or this one may just have reported
    // activity). A 401 here ends the sign-in with the right reason.
    useEffect(() => {
        if (!isAuthenticated || !phase.over) return;
        if (Date.now() - checkedAtRef.current < CHECK_EVERY_MS) return;
        checkedAtRef.current = Date.now();
        refresh();
    }, [isAuthenticated, phase.over, now, refresh]);

    const stayLoggedIn = useCallback(() => {
        trackerRef.current?.now();
    }, []);

    const signInAgain = useCallback(() => logOut({ returnToPage: true }), [logOut]);

    const value = useMemo(() => ({ session, phase, now: serverNow(session, now), refresh }), [session, phase, now, refresh]);

    const capKey = phase.capEndsAt || null;
    // "Keep working" hides it, but it comes back for the last minute.
    const capVisible = showWarning && phase.kind === "cap" && (capDismissedFor !== capKey || phase.leftMs <= 60 * 1000);
    const idleVisible = showWarning && phase.kind === "idle";

    return (
        <SessionContext.Provider value={value}>
            {children}
            {(idleVisible || capVisible) && (
                <SessionWarning
                    kind={phase.kind}
                    leftMs={phase.leftMs}
                    endsAt={phase.endsAt}
                    onStay={stayLoggedIn}
                    onSignOut={() => logOut()}
                    onSignInAgain={signInAgain}
                    onDismiss={() => setCapDismissedFor(capKey)}
                />
            )}
        </SessionContext.Provider>
    );
}

export default SessionProvider;

export const useWebSession = () => useContext(SessionContext) || { session: null, phase: { kind: null, endsAt: null, leftMs: null, capEndsAt: null, warn: false, over: false }, now: Date.now(), refresh: () => {} };
